import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faHourglassEmpty, faStar as faStarOutline } from '@fortawesome/free-regular-svg-icons';
import { faCaretDown, faCaretUp, faChevronLeft, faChevronRight, faDisplay, faEllipsisVertical, faHourglassEnd, faSpinner, faStar as faStarFilled, faTrash, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AllHtmlEntities } from 'html-entities';
import { JSX, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { produceEncodedTextWithImplicitLinks, lookupExactMatch } from '../biz/linkFinder';
import ImageShowcase from '../image/ImageShowcase';
import { SquiggleItem } from '../types/types';
import './ViewItem.css';
import { useAdventureDataContext } from '../context/AdventureDataContext';
import { formatMarkup } from '../biz/markdownLite';
import backend from '../backend';
import { Preview, useLinkHoverPreview } from './LinkHoverPreview';

interface ShowText {
  __html: string;
}

const entities = new AllHtmlEntities();

function ViewItem({
  item,
  isSelected,
  edit,
  editLinks,
  editAliases,
  deleteItem,
  toggleArchiveItem,
  toggleStarItem,
  link,
  uploadImage,
  removeImage,
  imageSeed,
  zoomImage,
  addNewChild
} : {
  item: SquiggleItem,
  isSelected: boolean,
  edit: () => void,
  editLinks: () => void,
  editAliases: () => void,
  deleteItem: (itemId: string) => void,
  toggleArchiveItem: (item: SquiggleItem) => void,
  toggleStarItem: (item: SquiggleItem) => void,
  link: (itemId: string, selectItem: boolean) => void,
  uploadImage: (file: File, itemId: string) => Promise<JSX.Element|null>,
  removeImage: (itemId: string, imageIndex: number) => Promise<void>,
  imageSeed: number,
  zoomImage: (item: SquiggleItem, imageIndex: number) => void,
  addNewChild: (parentId: string, type: "quest"|"personororg"|"place"|"miscitem"|"logentry"|"system") => void
}) {
  const { itemLookupTree, allItems, itemById } = useAdventureDataContext();

  const [showText, setShowText] = useState<ShowText>({ __html: entities.encode(item.text) });
  const [showcaseImage, setShowcaseImage] = useState<boolean>(false);
  const [imageSize, setImageSize] = useState<"min"|"med"|"max">("min");
  const [showImageRow, setShowImageRow] = useState<boolean>(false);
  const [imageUploadFile, setImageUploadFile] = useState<File>();
  const [isUploadingImage, setIsUploadingImage] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<JSX.Element|null>(null);
  const [imageIndex, setImageIndex] = useState<number>(0);
  const itemTextRef = useRef<HTMLDivElement>(null);

  const imageUrl = item.images.length > imageIndex && item.images.length > 0 ? backend.getImageUrl(item.images[imageIndex].imageFileName, imageSeed) : undefined;

  const children = useMemo(() =>
    allItems.filter(i => i.parent === item.id).sort((a, b) => a.name.localeCompare(b.name)),
    [allItems, item]
  );

  const { preview, style, onMouseEnter, onMouseMove, onMouseLeave } = useLinkHoverPreview(itemById, imageSeed);

  const childList = useMemo(() =>
    children.length === 0
      ? null
      :
      <div className="children">
        <h4 className="childheader">Children {item.type !== "system" && <div className="addchild" onClick={(e) => { addNewChild(item.id, item.type); e.stopPropagation(); }}>+</div>}</h4>
        <ul>
          {children.map(item => <li key={item.id}><a href="#" data-id={item.id} className={itemById.get(item.id)?.type} onClick={() => link(item.id, true)} onMouseOver={onMouseEnter} onMouseMove={onMouseMove} onMouseOut={onMouseLeave}>{item.name}</a></li>)}
        </ul>
      </div>,
    [addNewChild, children, itemById, link, item.id, item.type, onMouseEnter, onMouseMove, onMouseLeave]
  );

  const resolveLocalLink = useCallback((linkTarget: string) => {
    if (itemById.has(linkTarget)) {
      return { id: linkTarget, type: itemById.get(linkTarget)!.type };
    }
    const lookupResult = lookupExactMatch(itemLookupTree, linkTarget);
    return lookupResult ? { id: lookupResult.id, type: itemById.get(lookupResult.id)!.type } : undefined;
  }, [itemById, itemLookupTree]);
  
  useEffect(() => {
    const textWithLinks = formatMarkup(produceEncodedTextWithImplicitLinks(itemLookupTree, item.text, item.id), resolveLocalLink);
    setShowText({ __html: textWithLinks });
  }, [item, itemLookupTree, resolveLocalLink]);

  useEffect(() => {
    setImageIndex(Math.max(0, item.images.length - 1));
  }, [item.images.length, setImageIndex]);

  const tryLinking = useCallback((e: MouseEvent<HTMLDivElement>) => {
    if (e.target && e.target instanceof HTMLAnchorElement && e.target.dataset.id) {
      link(e.target.dataset.id, !e.shiftKey);
      e.stopPropagation();
      e.preventDefault();
    }
  }, [link]);

  const deleteCallback = useCallback(() => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      deleteItem(item.id);
    }
  }, [item, deleteItem]);

  const uploadImageCallback = useCallback(() => {
    (async() => {
      if (imageUploadFile && !isUploadingImage) {
        setIsUploadingImage(true);
        try {
          const result = await uploadImage(imageUploadFile, item.id);
          if (result) {
            setErrorMessage(result);
          }
          setShowImageRow(false);
          setImageUploadFile(undefined);
        } finally {
          setIsUploadingImage(false);
        }
      }
    })();
  }, [item.id, imageUploadFile, uploadImage, setImageUploadFile, setShowImageRow, isUploadingImage]);

  useEffect(() => {
    if (!isSelected) {
      return;
    }

    const listener = (e: KeyboardEvent) => {
      if ((e.key === 'E' || e.key === "e") && !e.ctrlKey && !e.metaKey) {
        e.stopPropagation();
        e.preventDefault();
        edit();
        return;
      }
      if ((e.key === 'L' || e.key === "l") && !e.ctrlKey && !e.metaKey) {
        e.stopPropagation();
        e.preventDefault();
        editLinks();
        return;
      }
      if ((e.key === 'N' || e.key === "n") && !e.ctrlKey && !e.metaKey) {
        e.stopPropagation();
        e.preventDefault();
        editAliases();
        return;
      }
      if (item.type !== "system" && (e.key === 'S' || e.key === "s") && !e.ctrlKey && !e.metaKey) {
        e.stopPropagation();
        e.preventDefault();
        toggleStarItem(item);
        return;
      }
      if (item.type !== "system" && (e.key === 'M' || e.key === "m") && !e.ctrlKey && !e.metaKey) {
        e.stopPropagation();
        e.preventDefault();
        if (!showImageRow && imageSize === "min") {
          setImageSize("med");
        }
        setShowImageRow(v => !v);
        return;
      }
      if (item.images.length > 0 && (e.key === 'I' || e.key === "i") && !e.ctrlKey && !e.metaKey) {
        e.stopPropagation();
        e.preventDefault();
        zoomImage(item, imageIndex);
        return;
      }
      if (item.images.length > 0 && document.fullscreenEnabled && (e.key === 'Z' || e.key === "z") && !e.ctrlKey && !e.metaKey) {
        e.stopPropagation();
        e.preventDefault();
        setShowcaseImage(true);
        return;
      }
      if (item.type !== "system" && e.key === 'Delete' && !e.ctrlKey && !e.metaKey) {
        e.stopPropagation();
        e.preventDefault();
        deleteCallback();
        return;
      }
      if (e.key === 'ArrowLeft' && !e.ctrlKey && !e.metaKey && item.images.length > 1) {
        e.stopPropagation();
        e.preventDefault();
        setImageIndex((prevIndex) => prevIndex === 0 ? item.images.length - 1 : prevIndex - 1);
        return;
      }
      if (e.key === 'ArrowRight' && !e.ctrlKey && !e.metaKey && item.images.length > 1) {
        e.stopPropagation();
        e.preventDefault();
        setImageIndex((prevIndex) => prevIndex === item.images.length - 1 ? 0 : prevIndex + 1);
        return;
      }
      if ((e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'PageUp' || e.key === 'PageDown' || e.key === 'Home' || e.key === 'End') && !e.ctrlKey && !e.metaKey) {
        if (itemTextRef.current && document.activeElement !== itemTextRef.current) {
          itemTextRef.current.focus({ preventScroll: true });
        }
        // Do not prevent default here; let the browser
        // handle scrolling based on the newly focused element.
        return;
      }
    };

    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [isSelected, edit, editLinks, editAliases, toggleStarItem, zoomImage, imageIndex, setShowImageRow, item, deleteCallback, item.images.length, setImageIndex, imageSize, setImageSize, showImageRow, setShowcaseImage, itemTextRef]);

  useEffect(() => {
    if (isSelected && itemTextRef.current) {
      itemTextRef.current.focus({ preventScroll: true });
    }
  }, [isSelected, itemTextRef]);

  return <>
    { showcaseImage && imageUrl && <ImageShowcase imageUrl={imageUrl} close={() => setShowcaseImage(false)} /> }
    <Preview preview={preview} style={style} />
    <div className="formrow namerow">
      <span className="name">{item.name}</span>
      <div className="aliasbutton" title="Names" onClick={editAliases}><FontAwesomeIcon icon={faEllipsisVertical as IconProp}></FontAwesomeIcon></div>
    </div>
    { imageSize !== "max" &&
      <div className="formrow grow view">
        <div
          className="itemtext"
          tabIndex={0}
          ref={itemTextRef}
          dangerouslySetInnerHTML={showText}
          onClick={tryLinking}
          onMouseOver={onMouseEnter}
          onMouseMove={onMouseMove}
          onMouseOut={onMouseLeave}
        />
        {childList}
      </div>
    }
    { item.images.length > 0 && imageUrl &&
      <div className="formrow">
        <div className={"image " + imageSize}>
          { imageSize !== "max" && <div className="showhidebutton larger" onClick={() => setImageSize(imageSize === "min" ? "med" : "max")} title="Larger image"><FontAwesomeIcon icon={faCaretUp as IconProp} /></div> }
          { imageSize !== "min" && <div className="showhidebutton smaller" onClick={() => setImageSize(imageSize === "max" ? "med" : "min")} title="Smaller image"><FontAwesomeIcon icon={faCaretDown as IconProp} /></div> }
          { document.fullscreenEnabled && <div className="showcasebutton" onClick={() => setShowcaseImage(true)} title="Showcase fullscreen [z]"><FontAwesomeIcon icon={faDisplay as IconProp} /></div> }
          { item.images.length > 1 && <div className="imageleft" onClick={() => setImageIndex((prevIndex) => prevIndex === 0 ? item.images.length - 1 : prevIndex - 1)}><FontAwesomeIcon icon={faChevronLeft as IconProp} /></div> }
          { item.images.length > 1 && <div className="imageright" onClick={() => setImageIndex((prevIndex) => prevIndex === item.images.length - 1 ? 0 : prevIndex + 1)}><FontAwesomeIcon icon={faChevronRight as IconProp} /></div> }
          <img className={item.images.length > 1 ? "multi" : ""} title="Show image and markers [i]" src={imageUrl} alt={item.name} onClick={() => zoomImage(item, imageIndex)}></img>
        </div>
      </div>
    }
    {
      showImageRow && item.type !== "system" && <>
      { item.images.length > 0 && <div className="formrow imagerow" style={{ flexDirection: "row", justifyContent: "center" }}>
        <button className="delete" onClick={() => removeImage(item.id, imageIndex)}>Remove this image and its markers</button>
      </div> }
      <div className="formrow imagerow">
        <label htmlFor="imageUpload" style={{ marginLeft: '0.2em', marginTop: '0.1em' }}>Add:</label>
        <input type="file" accept=".png, .apng, .avif, .jpg, .jpeg, .gif, .webp, .svg" onChange={(e) => setImageUploadFile(e.target.files![0])}></input>
        { isUploadingImage && <span className="uploadspinner"><FontAwesomeIcon icon={faSpinner as IconProp} spin /></span> }
        <button onClick={uploadImageCallback} disabled={!imageUploadFile || isUploadingImage}>{isUploadingImage ? "Uploading..." : "Upload"}</button>
      </div>
      </>
    }
    {
      errorMessage && <div className="formrow">
        <div className="errorMessage">
          <div className="closeError" onClick={() => setErrorMessage(null)}><FontAwesomeIcon icon={faXmark as IconProp}></FontAwesomeIcon></div>
          <label className="errorLabel">Error:</label>
          {errorMessage}
        </div>
      </div>
    }
    <div className="buttonrow reverse">
      <div className="buttoncontainer">
        <button className={item.type === "system" ? "disabled" : ""} onClick={() => { if (!showImageRow && imageSize === "min") { setImageSize("med"); } setShowImageRow(v => !v); }}>I<span className="hotkey">m</span>ages</button>
        <button onClick={editLinks}><span className="hotkey">L</span>inks</button>
        <button onClick={edit}><span className="hotkey">E</span>dit</button>
      </div>
      <div className="buttoncontainer grow">
        <button className={(item.starred ? "starred" : "") + " " + (item.type === "system" ? "disabled" : "")} title={item.starred ? "Unstar item [s]" : "Star item [s]"} onClick={() => item.type !== "system" && toggleStarItem(item)}><FontAwesomeIcon icon={(item.starred ? faStarFilled : faStarOutline) as IconProp}></FontAwesomeIcon></button>
        <button className={(item.archived ? "archived" : "") + " " + (item.type === "system" ? "disabled" : "")} title={item.archived ? "Unarchive" : "Archive"} onClick={() => item.type !== "system" && toggleArchiveItem(item)}><FontAwesomeIcon icon={(item.archived ? faHourglassEnd : faHourglassEmpty) as IconProp} /></button>
        <button title="Delete [Delete]" className={"delete " + (item.type === "system" ? " disabled" : "")} onClick={() => item.type !== "system" && deleteCallback()}><FontAwesomeIcon icon={faTrash as IconProp}></FontAwesomeIcon></button>
      </div>
    </div>
  </>;
}

export default ViewItem;